import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import VoiceRecorder from '../../components/common/VoiceRecorder';
import ChronoLauncher from '../../components/timer/ChronoLauncher';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  ArrowLeft,
  Wrench,
  Clock,
  Euro,
  Calendar,
  Globe,
  CheckCircle2,
  Play,
  Plus,
  FileText,
  BarChart3,
  Users,
  Loader2,
  Download,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Timer,
  Key,
  Eye,
  EyeOff,
  Copy,
  Share2,
  Trash2,
  Server,
  Link2,
  BarChart,
  MoreHorizontal,
  Camera,
  Image as ImageIcon
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';
import Avatar from '../../components/common/Avatar';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const ContractDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAgency } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [reports, setReports] = useState([]);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [expandedReports, setExpandedReports] = useState({});

  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    task_type: 'initiative',
    scheduled_date: ''
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [contractRes, teamRes] = await Promise.all([
        api.get(`/contracts/${id}`),
        isAgency ? api.get('/users/', { params: { role: 'team' } }) : Promise.resolve({ data: [] })
      ]);
      setContract(contractRes.data);
      setTasks(contractRes.data.tasks || []);
      setTeamMembers(teamRes.data);
      
      // Generate reports from tasks grouped by month
      generateReportsFromTasks(contractRes.data.tasks || [], teamRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const generateReportsFromTasks = (tasksList, members) => {
    // Group completed tasks by month
    const tasksByMonth = {};
    
    tasksList.forEach(task => {
      if (task.status === 'completed' && task.completed_at) {
        const date = new Date(task.completed_at);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const monthLabel = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
        
        if (!tasksByMonth[monthKey]) {
          tasksByMonth[monthKey] = {
            id: monthKey,
            month: monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1),
            monthKey,
            tasks: [],
            hours: 0,
            status: 'completed'
          };
        }
        
        // Find assignee
        const assignee = task.assignee_id ? members.find(m => m.id === task.assignee_id) : null;
        
        tasksByMonth[monthKey].tasks.push({
          ...task,
          assignee
        });
        tasksByMonth[monthKey].hours += (task.duration_minutes || 0) / 60;
      }
    });
    
    // Convert to array and sort by date (most recent first)
    const reportsArray = Object.values(tasksByMonth)
      .sort((a, b) => b.monthKey.localeCompare(a.monthKey));
    
    // Add mock data if no real reports (for demo)
    if (reportsArray.length === 0) {
      const mockReports = [
        { 
          id: 'dec-2024', 
          month: 'Décembre 2024', 
          monthKey: '2024-12',
          hours: 2.5, 
          status: 'completed',
          tasks: [
            { 
              id: 't1', 
              title: 'Mise à jour WordPress 6.4', 
              duration_minutes: 45, 
              completed_at: '2024-12-15T10:30:00',
              task_type: 'initiative',
              description: 'Mise à jour du core WordPress et des plugins',
              screenshot_before: null,
              screenshot_after: null,
              completion_notes: 'Mise à jour effectuée sans problème, sauvegarde réalisée avant intervention.',
              assignee: { first_name: 'Marie', last_name: 'Dupont' }
            },
            { 
              id: 't2', 
              title: 'Optimisation des images', 
              duration_minutes: 60, 
              completed_at: '2024-12-18T14:00:00',
              task_type: 'initiative',
              description: 'Compression et optimisation de toutes les images',
              screenshot_before: 'https://placehold.co/400x300/f1f5f9/64748b?text=Avant',
              screenshot_after: 'https://placehold.co/400x300/dcfce7/22c55e?text=Après',
              completion_notes: 'Gain de 45% sur le poids total des images.',
              assignee: { first_name: 'Pierre', last_name: 'Martin' }
            },
            { 
              id: 't3', 
              title: 'Correction bug formulaire contact', 
              duration_minutes: 30, 
              completed_at: '2024-12-20T09:15:00',
              task_type: 'client_request',
              description: 'Le formulaire ne s\'envoyait plus',
              screenshot_before: null,
              screenshot_after: null,
              completion_notes: 'Configuration SMTP corrigée.',
              assignee: { first_name: 'Marie', last_name: 'Dupont' }
            }
          ]
        },
        { 
          id: 'nov-2024', 
          month: 'Novembre 2024', 
          monthKey: '2024-11',
          hours: 1.8, 
          status: 'completed',
          tasks: [
            { 
              id: 't4', 
              title: 'Audit de sécurité', 
              duration_minutes: 90, 
              completed_at: '2024-11-10T11:00:00',
              task_type: 'initiative',
              description: 'Scan de sécurité complet du site',
              screenshot_before: null,
              screenshot_after: null,
              completion_notes: 'Aucune vulnérabilité détectée. Certificat SSL renouvelé.',
              assignee: { first_name: 'Pierre', last_name: 'Martin' }
            },
            { 
              id: 't5', 
              title: 'Sauvegarde mensuelle', 
              duration_minutes: 20, 
              completed_at: '2024-11-01T08:00:00',
              task_type: 'initiative',
              description: 'Sauvegarde complète BDD + fichiers',
              screenshot_before: null,
              screenshot_after: null,
              completion_notes: 'Sauvegarde stockée sur serveur distant.',
              assignee: { first_name: 'Marie', last_name: 'Dupont' }
            }
          ]
        },
        { 
          id: 'oct-2024', 
          month: 'Octobre 2024', 
          monthKey: '2024-10',
          hours: 3.2, 
          status: 'completed',
          tasks: [
            { 
              id: 't6', 
              title: 'Refonte page d\'accueil', 
              duration_minutes: 150, 
              completed_at: '2024-10-20T16:00:00',
              task_type: 'client_request',
              description: 'Nouvelle mise en page demandée par le client',
              screenshot_before: 'https://placehold.co/400x300/fef2f2/ef4444?text=Ancienne+version',
              screenshot_after: 'https://placehold.co/400x300/dcfce7/22c55e?text=Nouvelle+version',
              completion_notes: 'Refonte validée par le client.',
              assignee: { first_name: 'Pierre', last_name: 'Martin' }
            },
            { 
              id: 't7', 
              title: 'Mise à jour plugins', 
              duration_minutes: 40, 
              completed_at: '2024-10-05T10:00:00',
              task_type: 'initiative',
              description: 'Mise à jour de sécurité des plugins',
              screenshot_before: null,
              screenshot_after: null,
              completion_notes: '12 plugins mis à jour.',
              assignee: { first_name: 'Marie', last_name: 'Dupont' }
            }
          ]
        }
      ];
      setReports(mockReports);
    } else {
      setReports(reportsArray);
    }
  };

  const toggleReport = (reportId) => {
    setExpandedReports(prev => ({
      ...prev,
      [reportId]: !prev[reportId]
    }));
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/contracts/tasks', {
        contract_id: id,
        ...newTask
      });
      toast.success('Tâche créée !');
      setShowTaskModal(false);
      setNewTask({ title: '', description: '', task_type: 'initiative', scheduled_date: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setCreating(false);
    }
  };

  const startTask = async (taskId) => {
    try {
      await api.post(`/contracts/tasks/${taskId}/start`);
      toast.success('Chronomètre démarré !');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const completeTask = async (taskId) => {
    const duration = prompt('Durée en minutes ?');
    if (!duration) return;
    try {
      await api.post(`/contracts/tasks/${taskId}/complete`, { duration_minutes: parseInt(duration) });
      toast.success('Tâche terminée !');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Generate chart data from tasks
  const getChartData = () => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const report = reports.find(r => r.monthKey === monthKey);
      months.push({
        month: date.toLocaleDateString('fr-FR', { month: 'short' }),
        hours: report?.hours || Math.random() * 3 + 0.5
      });
    }
    return months;
  };

  const chartData = getChartData();
  const maxHours = Math.max(...chartData.map(d => d.hours));

  const formatDuration = (minutes) => {
    if (!minutes) return '0 min';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  // Generate PDF report for a specific month
  const generateReportPDF = (report) => {
    try {
      const doc = new jsPDF();
      const primaryColor = theme.colors?.primary || '#1a365d';
      const agencyName = theme.agency?.name || 'MSC L\'AGENCE';
      
      // Header
      doc.setFillColor(26, 54, 93); // Primary color
      doc.rect(0, 0, 220, 40, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text(agencyName, 15, 20);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('Rapport d\'activité mensuel', 15, 30);
      
      // Contract & Period info
      doc.setTextColor(26, 54, 93);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(`Contrat: ${contract?.name || contract?.title || 'N/A'}`, 15, 55);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(`Période: ${report.month}`, 15, 65);
      doc.text(`Client: ${contract?.client?.company_name || contract?.client?.first_name + ' ' + contract?.client?.last_name || 'N/A'}`, 15, 73);
      
      // Summary box
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(15, 85, 180, 30, 3, 3, 'F');
      
      doc.setTextColor(26, 54, 93);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Résumé', 20, 97);
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      const totalHours = report.hours?.toFixed(1) || '0';
      const taskCount = report.tasks?.length || 0;
      doc.text(`Temps total: ${totalHours}h  |  Tâches réalisées: ${taskCount}`, 20, 107);
      
      // Tasks table
      doc.setTextColor(26, 54, 93);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Détail des interventions', 15, 130);
      
      const tableData = (report.tasks || []).map(task => [
        task.title || 'Tâche',
        task.assignee ? `${task.assignee.first_name} ${task.assignee.last_name}` : 'Non assigné',
        formatDuration(task.duration_minutes),
        task.completed_at ? new Date(task.completed_at).toLocaleDateString('fr-FR') : '-',
        task.proof_comment || '-'
      ]);
      
      doc.autoTable({
        startY: 135,
        head: [['Tâche', 'Intervenant', 'Durée', 'Date', 'Commentaire']],
        body: tableData.length > 0 ? tableData : [['Aucune tâche', '-', '-', '-', '-']],
        theme: 'grid',
        headStyles: {
          fillColor: [26, 54, 93],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 10
        },
        bodyStyles: {
          fontSize: 9,
          textColor: [51, 65, 85]
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252]
        },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 35 },
          2: { cellWidth: 25 },
          3: { cellWidth: 25 },
          4: { cellWidth: 45 }
        },
        margin: { left: 15, right: 15 }
      });
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(9);
        doc.setTextColor(148, 163, 184);
        doc.text(
          `Généré le ${new Date().toLocaleDateString('fr-FR')} - Page ${i}/${pageCount}`,
          105,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }
      
      // Download
      const fileName = `rapport_${(contract?.name || contract?.title)?.replace(/\s+/g, '_') || 'contrat'}_${report.monthKey}.pdf`;
      doc.save(fileName);
      toast.success('Rapport PDF téléchargé !');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  // Get unique team members from report tasks
  const getReportAssignees = (report) => {
    const assignees = new Map();
    report.tasks.forEach(task => {
      if (task.assignee) {
        assignees.set(task.assignee.first_name + task.assignee.last_name, task.assignee);
      }
    });
    return Array.from(assignees.values());
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#64748b]" />
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <p className="text-[#64748b]">Contrat non trouvé</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />

      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Back button & Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate('/contracts')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                {contract.name}
              </h1>
              <Badge className={contract.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                {contract.status === 'active' ? 'Actif' : 'Inactif'}
              </Badge>
            </div>
            {contract.website_url && (
              <a 
                href={contract.website_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm hover:underline"
                style={{ color: theme.colors?.accent }}
              >
                <Globe className="w-3 h-3 inline mr-1" />
                {contract.website_url}
              </a>
            )}
          </div>
          {isAgency && (
            <Button
              onClick={() => setShowTaskModal(true)}
              className="rounded-full text-white"
              style={{ backgroundColor: theme.colors?.accent }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle tâche
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${theme.colors?.accent}20` }}>
                  <Euro className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">Prix mensuel</p>
                  <p className="text-xl font-bold" style={{ color: theme.colors?.primary }}>
                    {(contract.monthly_price || 0).toLocaleString('fr-FR')}€
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-100">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">Heures utilisées</p>
                  <p className="text-xl font-bold" style={{ color: theme.colors?.primary }}>
                    {(contract.hours_used || 0).toFixed(1)}h / {contract.hours_included || 0}h
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-100">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">Tâches terminées</p>
                  <p className="text-xl font-bold" style={{ color: theme.colors?.primary }}>
                    {tasks.filter(t => t.status === 'completed').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-purple-100">
                  <Calendar className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">Engagement</p>
                  <p className="text-xl font-bold" style={{ color: theme.colors?.primary }}>
                    {contract.engagement}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="tasks" className="space-y-6">
          <TabsList className="bg-white border flex-wrap">
            <TabsTrigger value="tasks" className="gap-2">
              <Wrench className="w-4 h-4" />
              Tâches
            </TabsTrigger>
            <TabsTrigger value="reports" className="gap-2">
              <FileText className="w-4 h-4" />
              Rapports
            </TabsTrigger>
            <TabsTrigger value="access" className="gap-2">
              <Key className="w-4 h-4" />
              Accès
            </TabsTrigger>
            <TabsTrigger value="analytics" className="gap-2">
              <BarChart3 className="w-4 h-4" />
              Statistiques
            </TabsTrigger>
          </TabsList>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <Card className="card">
              <CardHeader className="py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Tâches de maintenance ({tasks.length})</CardTitle>
                    <CardDescription className="text-sm">Interventions sur ce contrat</CardDescription>
                  </div>
                  {isAgency && (
                    <Button variant="outline" size="sm" onClick={() => setShowTaskModal(true)}>
                      <Plus className="w-4 h-4 mr-1" />
                      Ajouter
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {tasks.length === 0 ? (
                  <div className="text-center py-12">
                    <Wrench className="w-12 h-12 mx-auto mb-4 text-[#64748b] opacity-50" />
                    <p className="text-[#64748b]">Aucune tâche pour ce contrat</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50/50 hover:bg-slate-50/50">
                          <TableHead className="text-xs font-medium w-[35%]">Tâche</TableHead>
                          <TableHead className="text-xs font-medium">Responsable</TableHead>
                          <TableHead className="text-xs font-medium text-center">Durée</TableHead>
                          <TableHead className="text-xs font-medium text-center">Date</TableHead>
                          <TableHead className="text-xs font-medium">Statut</TableHead>
                          {isAgency && <TableHead className="text-xs font-medium text-right">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tasks.map((task) => {
                          const assignee = task.assignee_id ? teamMembers.find(m => m.id === task.assignee_id) : null;
                          return (
                            <TableRow key={task.id} className={cn(
                              task.status === 'completed' && "bg-green-50/30",
                              task.status === 'in_progress' && "bg-blue-50/30"
                            )}>
                              <TableCell className="py-2">
                                <div className="flex items-center gap-2">
                                  {task.task_type === 'client_request' && (
                                    <Badge className="bg-orange-100 text-orange-700 text-[10px] h-5">Client</Badge>
                                  )}
                                  <span className="font-medium text-sm">{task.title}</span>
                                </div>
                              </TableCell>
                              <TableCell className="py-2">
                                {assignee ? (
                                  <div className="flex items-center gap-2">
                                    <Avatar name={`${assignee.first_name} ${assignee.last_name}`} size="xs" />
                                    <span className="text-sm text-slate-600">{assignee.first_name} {assignee.last_name}</span>
                                  </div>
                                ) : (
                                  <span className="text-sm text-slate-400 italic">Non assigné</span>
                                )}
                              </TableCell>
                              <TableCell className="py-2 text-center">
                                <span className="text-xs font-mono text-slate-600">
                                  {task.duration_minutes ? `${task.duration_minutes}min` : '-'}
                                </span>
                              </TableCell>
                              <TableCell className="py-2 text-center">
                                <span className="text-xs text-slate-500">
                                  {task.scheduled_date ? task.scheduled_date.split('T')[0] : '-'}
                                </span>
                              </TableCell>
                              <TableCell className="py-2">
                                <Badge className={cn(
                                  "text-[10px] h-5",
                                  task.status === 'completed' && 'bg-green-100 text-green-700',
                                  task.status === 'in_progress' && 'bg-blue-100 text-blue-700',
                                  task.status === 'scheduled' && 'bg-purple-100 text-purple-700',
                                  task.status === 'pending' && 'bg-slate-100 text-slate-700'
                                )}>
                                  {task.status === 'completed' ? 'Terminé' : 
                                   task.status === 'in_progress' ? 'En cours' : 
                                   task.status === 'scheduled' ? 'Planifié' : 'En attente'}
                                </Badge>
                              </TableCell>
                              {isAgency && (
                                <TableCell className="py-2 text-right">
                                  {task.status !== 'completed' && (
                                    <div className="flex items-center justify-end gap-1">
                                      {/* Chrono launcher */}
                                      {task.status === 'in_progress' && (
                                        <ChronoLauncher
                                          taskId={task.id}
                                          contractId={id}
                                          taskTitle={task.title}
                                          size="sm"
                                          variant="ghost"
                                          showLabel={false}
                                        />
                                      )}
                                      {(task.status === 'pending' || task.status === 'scheduled') && (
                                        <Button 
                                          size="sm" 
                                          variant="ghost"
                                          className="h-7 text-xs"
                                          onClick={() => startTask(task.id)}
                                        >
                                          <Play className="w-3 h-3 mr-1" />
                                          Démarrer
                                        </Button>
                                      )}
                                      {task.status === 'in_progress' && (
                                        <Button 
                                          size="sm" 
                                          className="h-7 text-xs bg-green-500 hover:bg-green-600"
                                          onClick={() => completeTask(task.id)}
                                        >
                                          <CheckCircle2 className="w-3 h-3 mr-1" />
                                          Terminer
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab - Expandable */}
          <TabsContent value="reports">
            <Card className="card">
              <CardHeader>
                <CardTitle>Rapports mensuels</CardTitle>
                <CardDescription>Cliquez sur un rapport pour voir le détail des interventions</CardDescription>
              </CardHeader>
              <CardContent>
                {reports.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-[#64748b] opacity-50" />
                    <p className="text-[#64748b]">Aucun rapport disponible</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {reports.map((report) => {
                      const isExpanded = expandedReports[report.id];
                      const assignees = getReportAssignees(report);
                      
                      return (
                        <div key={report.id} className="rounded-xl border border-[#e2e8f0] overflow-hidden">
                          {/* Report Header - Clickable */}
                          <button
                            onClick={() => toggleReport(report.id)}
                            className="w-full flex items-center justify-between p-4 hover:bg-[#f8fafc] transition-colors text-left"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#f1f5f9]">
                                <FileText className="w-5 h-5 text-[#64748b]" />
                              </div>
                              <div>
                                <h4 className="font-medium" style={{ color: theme.colors?.primary }}>{report.month}</h4>
                                <p className="text-sm text-[#64748b]">{report.tasks.length} tâches • {report.hours.toFixed(1)}h</p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-4">
                              {/* Avatar Stack - Monday style */}
                              <div className="flex -space-x-2">
                                {assignees.slice(0, 3).map((assignee, idx) => (
                                  <div 
                                    key={idx} 
                                    className="relative"
                                    style={{ zIndex: assignees.length - idx }}
                                  >
                                    <Avatar
                                      name={`${assignee.first_name} ${assignee.last_name}`}
                                      size="sm"
                                      className="ring-2 ring-white"
                                    />
                                  </div>
                                ))}
                                {assignees.length > 3 && (
                                  <div className="w-8 h-8 rounded-full bg-[#e2e8f0] flex items-center justify-center text-xs font-medium text-[#64748b] ring-2 ring-white">
                                    +{assignees.length - 3}
                                  </div>
                                )}
                              </div>
                              
                              <Badge className="bg-green-100 text-green-700">Validé</Badge>
                              
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="rounded-full" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  generateReportPDF(report);
                                }}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                PDF
                              </Button>
                              
                              {isExpanded ? (
                                <ChevronDown className="w-5 h-5 text-[#64748b]" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-[#64748b]" />
                              )}
                            </div>
                          </button>
                          
                          {/* Expanded Content - Task Details */}
                          {isExpanded && (
                            <div className="border-t border-[#e2e8f0] bg-[#f8fafc]">
                              <div className="p-4">
                                <table className="w-full">
                                  <thead>
                                    <tr className="text-xs text-[#64748b] uppercase tracking-wider">
                                      <th className="text-left pb-3 font-medium">Tâche</th>
                                      <th className="text-left pb-3 font-medium">Intervenant</th>
                                      <th className="text-left pb-3 font-medium">Durée</th>
                                      <th className="text-left pb-3 font-medium">Preuves / Commentaire</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-[#e2e8f0]">
                                    {report.tasks.map((task) => (
                                      <tr key={task.id} className="bg-white">
                                        <td className="py-4 pr-4">
                                          <div className="flex items-start gap-3">
                                            <div>
                                              <p className="font-medium" style={{ color: theme.colors?.primary }}>
                                                {task.title}
                                              </p>
                                              {task.description && (
                                                <p className="text-sm text-[#64748b] mt-0.5">{task.description}</p>
                                              )}
                                              <div className="flex items-center gap-2 mt-1">
                                                {task.task_type === 'client_request' && (
                                                  <Badge className="bg-orange-100 text-orange-700 text-[10px]">Demande client</Badge>
                                                )}
                                                <span className="text-xs text-[#94a3b8]">
                                                  {new Date(task.completed_at).toLocaleDateString('fr-FR')}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </td>
                                        <td className="py-4 pr-4">
                                          {task.assignee && (
                                            <div className="flex items-center gap-2">
                                              <Avatar
                                                name={`${task.assignee.first_name} ${task.assignee.last_name}`}
                                                size="sm"
                                              />
                                              <span className="text-sm font-medium" style={{ color: theme.colors?.primary }}>
                                                {task.assignee.first_name}
                                              </span>
                                            </div>
                                          )}
                                        </td>
                                        <td className="py-4 pr-4">
                                          <div className="flex items-center gap-1.5">
                                            <Timer className="w-4 h-4 text-[#64748b]" />
                                            <span className="font-mono text-sm" style={{ color: theme.colors?.primary }}>
                                              {formatDuration(task.duration_minutes)}
                                            </span>
                                          </div>
                                        </td>
                                        <td className="py-4">
                                          {/* Screenshots or Comment */}
                                          {(task.screenshot_before || task.screenshot_after) ? (
                                            <div className="flex items-center gap-3">
                                              {task.screenshot_before && (
                                                <div className="relative group">
                                                  <img 
                                                    src={task.screenshot_before} 
                                                    alt="Avant" 
                                                    className="w-16 h-12 object-cover rounded border border-red-200 cursor-pointer hover:scale-105 transition-transform"
                                                  />
                                                  <span className="absolute -bottom-1 -right-1 bg-red-500 text-white text-[8px] px-1 rounded">
                                                    AVANT
                                                  </span>
                                                </div>
                                              )}
                                              {task.screenshot_after && (
                                                <div className="relative group">
                                                  <img 
                                                    src={task.screenshot_after} 
                                                    alt="Après" 
                                                    className="w-16 h-12 object-cover rounded border border-green-200 cursor-pointer hover:scale-105 transition-transform"
                                                  />
                                                  <span className="absolute -bottom-1 -right-1 bg-green-500 text-white text-[8px] px-1 rounded">
                                                    APRÈS
                                                  </span>
                                                </div>
                                              )}
                                            </div>
                                          ) : task.completion_notes ? (
                                            <div className="flex items-start gap-2 max-w-xs">
                                              <MessageSquare className="w-4 h-4 text-[#64748b] flex-shrink-0 mt-0.5" />
                                              <p className="text-sm text-[#64748b] line-clamp-2">
                                                {task.completion_notes}
                                              </p>
                                            </div>
                                          ) : (
                                            <span className="text-sm text-[#94a3b8] italic">Aucune preuve</span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                
                                {/* Report Summary */}
                                <div className="mt-4 pt-4 border-t border-[#e2e8f0] flex items-center justify-between">
                                  <div className="flex items-center gap-6 text-sm">
                                    <span className="text-[#64748b]">
                                      Total : <span className="font-bold" style={{ color: theme.colors?.primary }}>{report.tasks.length} tâches</span>
                                    </span>
                                    <span className="text-[#64748b]">
                                      Durée totale : <span className="font-bold" style={{ color: theme.colors?.accent }}>{report.hours.toFixed(1)}h</span>
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {assignees.map((assignee, idx) => (
                                      <span key={idx} className="text-xs text-[#64748b]">
                                        {idx > 0 && ', '}
                                        {assignee.first_name} {assignee.last_name}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Access Tab */}
          <TabsContent value="access">
            <AccessTab 
              contractId={id} 
              theme={theme} 
              isAgency={isAgency} 
              teamMembers={teamMembers}
            />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <Card className="card">
              <CardHeader>
                <CardTitle>Évolution des interventions</CardTitle>
                <CardDescription>Heures de maintenance par mois</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-end justify-between gap-2 px-4">
                  {chartData.map((data, index) => (
                    <div key={index} className="flex-1 flex flex-col items-center gap-2">
                      <div 
                        className="w-full rounded-t-lg transition-all hover:opacity-80"
                        style={{ 
                          height: `${(data.hours / maxHours) * 180}px`,
                          backgroundColor: theme.colors?.accent,
                          minHeight: '20px'
                        }}
                      />
                      <span className="text-xs text-[#64748b]">{data.month}</span>
                      <span className="text-xs font-medium" style={{ color: theme.colors?.primary }}>
                        {data.hours.toFixed(1)}h
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create Task Modal */}
        <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
          <DialogContent aria-describedby="task-desc">
            <DialogHeader>
              <DialogTitle style={{ color: theme.colors?.primary }}>Nouvelle tâche</DialogTitle>
            </DialogHeader>
            <p id="task-desc" className="sr-only">Créer une tâche de maintenance</p>
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div className="space-y-2">
                <Label>Titre *</Label>
                <Input
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="Ex: Mise à jour WordPress"
                  className="input-default"
                  required
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Description</Label>
                  <VoiceRecorder 
                    onTranscription={(text) => setNewTask({ ...newTask, description: newTask.description ? `${newTask.description} ${text}` : text })}
                    language="fr"
                  />
                </div>
                <Textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Détails de l'intervention... (ou utilisez la dictée vocale)"
                  className="input-default"
                  rows={4}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <select
                    value={newTask.task_type}
                    onChange={(e) => setNewTask({ ...newTask, task_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-xl"
                  >
                    <option value="initiative">Initiative agence</option>
                    <option value="client_request">Demande client</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Date prévue</Label>
                  <Input
                    type="date"
                    value={newTask.scheduled_date}
                    onChange={(e) => setNewTask({ ...newTask, scheduled_date: e.target.value })}
                    className="input-default"
                  />
                </div>
              </div>
              
              {/* Photos Avant/Après */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Photos avant/après (optionnel)
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-[#64748b]">Photo AVANT</p>
                    <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                      {newTask.photo_before ? (
                        <div className="relative w-full h-full">
                          <img src={URL.createObjectURL(newTask.photo_before)} alt="Avant" className="w-full h-full object-cover rounded-lg" />
                          <button 
                            type="button"
                            onClick={(e) => { e.preventDefault(); setNewTask({ ...newTask, photo_before: null }); }}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                          >×</button>
                        </div>
                      ) : (
                        <>
                          <ImageIcon className="w-6 h-6 text-slate-400 mb-1" />
                          <span className="text-xs text-slate-500">Cliquez pour ajouter</span>
                        </>
                      )}
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden"
                        onChange={(e) => setNewTask({ ...newTask, photo_before: e.target.files[0] })}
                      />
                    </label>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-[#64748b]">Photo APRÈS</p>
                    <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                      {newTask.photo_after ? (
                        <div className="relative w-full h-full">
                          <img src={URL.createObjectURL(newTask.photo_after)} alt="Après" className="w-full h-full object-cover rounded-lg" />
                          <button 
                            type="button"
                            onClick={(e) => { e.preventDefault(); setNewTask({ ...newTask, photo_after: null }); }}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                          >×</button>
                        </div>
                      ) : (
                        <>
                          <ImageIcon className="w-6 h-6 text-slate-400 mb-1" />
                          <span className="text-xs text-slate-500">Cliquez pour ajouter</span>
                        </>
                      )}
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden"
                        onChange={(e) => setNewTask({ ...newTask, photo_after: e.target.files[0] })}
                      />
                    </label>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowTaskModal(false)} className="rounded-full">
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="rounded-full text-white" 
                  style={{ backgroundColor: theme.colors?.accent }}
                  disabled={creating}
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Créer
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

// Task Card Component with Avatar Stack (like Monday.com)
const TaskCard = ({ task, teamMembers, onStart, onComplete, theme, isAgency }) => {
  const assignee = task.assignee_id ? teamMembers.find(m => m.id === task.assignee_id) : null;
  
  const getStatusBadge = (status) => {
    const styles = {
      completed: { bg: 'bg-green-100', color: 'text-green-700', label: 'Terminé' },
      in_progress: { bg: 'bg-blue-100', color: 'text-blue-700', label: 'En cours' },
      scheduled: { bg: 'bg-purple-100', color: 'text-purple-700', label: 'Planifié' },
      pending: { bg: 'bg-gray-100', color: 'text-gray-700', label: 'En attente' }
    };
    const style = styles[status] || styles.pending;
    return <Badge className={`${style.bg} ${style.color}`}>{style.label}</Badge>;
  };

  return (
    <div className="flex items-center justify-between p-4 rounded-xl border border-[#e2e8f0] hover:border-[#cbd5e1] transition-colors bg-white">
      <div className="flex items-center gap-4">
        {/* Avatar Stack */}
        <div className="flex -space-x-2">
          {assignee ? (
            <div className="relative">
              <Avatar
                name={`${assignee.first_name} ${assignee.last_name}`}
                size="sm"
                className="ring-2 ring-white"
              />
            </div>
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#f1f5f9] flex items-center justify-center ring-2 ring-white">
              <Users className="w-4 h-4 text-[#94a3b8]" />
            </div>
          )}
        </div>
        
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium" style={{ color: theme.colors?.primary }}>{task.title}</h4>
            {getStatusBadge(task.status)}
          </div>
          <div className="flex items-center gap-3 text-xs text-[#94a3b8]">
            {assignee && <span>{assignee.first_name} {assignee.last_name}</span>}
            {task.scheduled_date && <span>📅 {task.scheduled_date.split('T')[0]}</span>}
            {task.duration_minutes > 0 && <span>⏱️ {task.duration_minutes} min</span>}
            {task.task_type === 'client_request' && <Badge className="bg-orange-100 text-orange-700 text-[10px]">Demande client</Badge>}
          </div>
        </div>
      </div>
      
      {isAgency && task.status !== 'completed' && (
        <div className="flex gap-2">
          {task.status === 'pending' || task.status === 'scheduled' ? (
            <Button
              size="sm"
              className="rounded-full text-white"
              style={{ backgroundColor: theme.colors?.accent }}
              onClick={() => onStart(task.id)}
            >
              <Play className="w-3 h-3 mr-1" />
              Démarrer
            </Button>
          ) : (
            <Button
              size="sm"
              className="rounded-full bg-green-500 text-white hover:bg-green-600"
              onClick={() => onComplete(task.id)}
            >
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Terminer
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// Access Tab Component
const AccessTab = ({ contractId, theme, isAgency, teamMembers }) => {
  const [accesses, setAccesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedAccess, setSelectedAccess] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});
  const [creating, setCreating] = useState(false);
  const [clients, setClients] = useState([]);

  const [newAccess, setNewAccess] = useState({
    access_type: 'wordpress',
    name: '',
    url: '',
    username: '',
    password: '',
    notes: ''
  });

  useEffect(() => {
    fetchAccesses();
    if (isAgency) {
      fetchClients();
    }
  }, [contractId]);

  const fetchAccesses = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/access/contract/${contractId}`);
      setAccesses(response.data);
    } catch (error) {
      console.error('Error:', error);
      // Demo data
      setAccesses([
        {
          id: 'demo-1',
          access_type: 'wordpress',
          name: 'Admin WordPress',
          url: 'https://www.monsite.fr/wp-admin',
          username: 'admin_msc',
          password: 'SecurePass123!',
          notes: 'Compte administrateur principal',
          shared_with: ['user-1', 'user-2'],
          created_by: 'agency',
          created_at: '2024-01-15T10:00:00',
          history: [
            { id: 'h1', action: 'created', user_name: 'Admin Agence', details: 'Accès créé', created_at: '2024-01-15T10:00:00' },
            { id: 'h2', action: 'shared', user_name: 'Admin Agence', details: 'Partagé avec : Marie Dupont', created_at: '2024-02-01T14:30:00' },
          ]
        },
        {
          id: 'demo-2',
          access_type: 'ftp',
          name: 'FTP Principal',
          url: 'ftp.monsite.fr',
          username: 'ftp_monsite',
          password: 'FtpSecure456!',
          notes: 'Accès FTP racine',
          shared_with: [],
          created_by: 'agency',
          created_at: '2024-01-15T10:05:00',
          history: []
        },
        {
          id: 'demo-3',
          access_type: 'hosting',
          name: 'Panel Hébergeur',
          url: 'https://panel.ovh.com',
          username: 'xx1234-ovh',
          password: 'HostingPass789!',
          notes: 'Accès OVH - Ne pas modifier les DNS',
          shared_with: [],
          created_by: 'agency',
          created_at: '2024-01-15T10:10:00',
          history: []
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/users/', { params: { role: 'client' } });
      setClients(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/access/', {
        contract_id: contractId,
        ...newAccess,
        shared_with: []
      });
      toast.success('Accès créé !');
      setShowAddModal(false);
      setNewAccess({ access_type: 'wordpress', name: '', url: '', username: '', password: '', notes: '' });
      fetchAccesses();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setCreating(false);
    }
  };

  const handleShare = async (accessId, userIds) => {
    try {
      await api.put(`/access/${accessId}/share`, { user_ids: userIds });
      toast.success('Accès partagé !');
      setShowShareModal(false);
      fetchAccesses();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleRevoke = async (accessId, userId) => {
    try {
      await api.put(`/access/${accessId}/revoke`, { user_ids: [userId] });
      toast.success('Accès révoqué');
      fetchAccesses();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (accessId) => {
    if (!window.confirm('Supprimer cet accès ?')) return;
    try {
      await api.delete(`/access/${accessId}`);
      toast.success('Accès supprimé');
      fetchAccesses();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié !`);
  };

  const togglePassword = (accessId) => {
    setShowPasswords(prev => ({ ...prev, [accessId]: !prev[accessId] }));
  };

  const getAccessIcon = (type) => {
    switch (type) {
      case 'wordpress': return <Globe className="w-5 h-5 text-blue-600" />;
      case 'ftp': return <Server className="w-5 h-5 text-green-600" />;
      case 'hosting': return <Server className="w-5 h-5 text-purple-600" />;
      case 'analytics': return <BarChart className="w-5 h-5 text-orange-600" />;
      default: return <Key className="w-5 h-5 text-gray-600" />;
    }
  };

  const getAccessTypeBg = (type) => {
    switch (type) {
      case 'wordpress': return 'bg-blue-100';
      case 'ftp': return 'bg-green-100';
      case 'hosting': return 'bg-purple-100';
      case 'analytics': return 'bg-orange-100';
      default: return 'bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#64748b]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Accès partagés</CardTitle>
            <CardDescription>Identifiants et accès aux différentes plateformes</CardDescription>
          </div>
          {isAgency && (
            <Button 
              onClick={() => setShowAddModal(true)}
              className="rounded-full text-white"
              style={{ backgroundColor: theme.colors?.accent }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvel accès
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {accesses.length === 0 ? (
            <div className="text-center py-12">
              <Key className="w-12 h-12 mx-auto mb-4 text-[#64748b] opacity-50" />
              <p className="text-[#64748b]">Aucun accès partagé</p>
            </div>
          ) : (
            <div className="space-y-4">
              {accesses.map((access) => (
                <div key={access.id} className="rounded-xl border border-[#e2e8f0] overflow-hidden">
                  {/* Access Header */}
                  <div className="p-4 bg-white">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", getAccessTypeBg(access.access_type))}>
                          {getAccessIcon(access.access_type)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium" style={{ color: theme.colors?.primary }}>{access.name}</h4>
                            <Badge className="bg-gray-100 text-gray-700 capitalize">{access.access_type}</Badge>
                          </div>
                          {access.url && (
                            <a 
                              href={access.url.startsWith('http') ? access.url : `https://${access.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm hover:underline flex items-center gap-1"
                              style={{ color: theme.colors?.accent }}
                            >
                              <Link2 className="w-3 h-3" />
                              {access.url}
                            </a>
                          )}
                        </div>
                      </div>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="rounded-full">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {isAgency && (
                            <>
                              <DropdownMenuItem onClick={() => { setSelectedAccess(access); setShowShareModal(true); }}>
                                <Share2 className="w-4 h-4 mr-2" />
                                Partager
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(access.id)} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    {/* Credentials */}
                    <div className="grid sm:grid-cols-2 gap-4 mt-4">
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-[#f8fafc]">
                        <span className="text-xs text-[#64748b] w-20">Identifiant</span>
                        <code className="flex-1 font-mono text-sm" style={{ color: theme.colors?.primary }}>{access.username}</code>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copyToClipboard(access.username, 'Identifiant')}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-[#f8fafc]">
                        <span className="text-xs text-[#64748b] w-20">Mot de passe</span>
                        <code className="flex-1 font-mono text-sm" style={{ color: theme.colors?.primary }}>
                          {showPasswords[access.id] ? access.password : '••••••••••'}
                        </code>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => togglePassword(access.id)}>
                          {showPasswords[access.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copyToClipboard(access.password, 'Mot de passe')}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    
                    {access.notes && (
                      <p className="text-sm text-[#64748b] mt-3 p-2 bg-yellow-50 rounded-lg border border-yellow-100">
                        💡 {access.notes}
                      </p>
                    )}
                    
                    {/* Shared With */}
                    {access.shared_with?.length > 0 && (
                      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[#e2e8f0]">
                        <Share2 className="w-4 h-4 text-[#64748b]" />
                        <span className="text-xs text-[#64748b]">Partagé avec :</span>
                        <div className="flex -space-x-2">
                          {access.shared_with.slice(0, 5).map((userId, idx) => {
                            const user = teamMembers.find(m => m.id === userId) || clients?.find(c => c.id === userId);
                            return user ? (
                              <Avatar 
                                key={idx}
                                name={`${user.first_name} ${user.last_name}`}
                                size="sm"
                                className="ring-2 ring-white"
                              />
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* History */}
                  {access.history?.length > 0 && (
                    <div className="border-t border-[#e2e8f0] bg-[#f8fafc] p-4">
                      <p className="text-xs font-semibold text-[#64748b] mb-2">Historique</p>
                      <div className="space-y-2">
                        {access.history.slice(0, 3).map((hist) => (
                          <div key={hist.id} className="flex items-center gap-2 text-xs text-[#64748b]">
                            <div className={cn(
                              "w-2 h-2 rounded-full",
                              hist.action === 'created' ? 'bg-green-500' : hist.action === 'shared' ? 'bg-blue-500' : 'bg-red-500'
                            )} />
                            <span className="font-medium">{hist.user_name}</span>
                            <span>{hist.details}</span>
                            <span className="ml-auto">{new Date(hist.created_at).toLocaleDateString('fr-FR')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Access Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent aria-describedby="add-access-desc">
          <DialogHeader>
            <DialogTitle style={{ color: theme.colors?.primary }}>Nouvel accès</DialogTitle>
          </DialogHeader>
          <p id="add-access-desc" className="sr-only">Ajouter un accès partagé</p>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <select
                  value={newAccess.access_type}
                  onChange={(e) => setNewAccess({ ...newAccess, access_type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-xl"
                >
                  <option value="wordpress">WordPress</option>
                  <option value="ftp">FTP</option>
                  <option value="hosting">Hébergeur</option>
                  <option value="analytics">Analytics</option>
                  <option value="other">Autre</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input
                  value={newAccess.name}
                  onChange={(e) => setNewAccess({ ...newAccess, name: e.target.value })}
                  placeholder="Admin WordPress"
                  className="input-default"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>URL</Label>
              <Input
                value={newAccess.url}
                onChange={(e) => setNewAccess({ ...newAccess, url: e.target.value })}
                placeholder="https://www.site.fr/wp-admin"
                className="input-default"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Identifiant *</Label>
                <Input
                  value={newAccess.username}
                  onChange={(e) => setNewAccess({ ...newAccess, username: e.target.value })}
                  placeholder="admin"
                  className="input-default"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Mot de passe *</Label>
                <Input
                  type="password"
                  value={newAccess.password}
                  onChange={(e) => setNewAccess({ ...newAccess, password: e.target.value })}
                  placeholder="••••••••"
                  className="input-default"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={newAccess.notes}
                onChange={(e) => setNewAccess({ ...newAccess, notes: e.target.value })}
                placeholder="Informations importantes..."
                className="input-default"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="rounded-full">
                Annuler
              </Button>
              <Button 
                type="submit" 
                className="rounded-full text-white" 
                style={{ backgroundColor: theme.colors?.accent }}
                disabled={creating}
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Créer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent aria-describedby="share-access-desc">
          <DialogHeader>
            <DialogTitle style={{ color: theme.colors?.primary }}>Partager l'accès</DialogTitle>
          </DialogHeader>
          <p id="share-access-desc" className="sr-only">Partager un accès</p>
          <div className="space-y-4">
            <p className="text-sm text-[#64748b]">
              Sélectionnez les utilisateurs avec qui partager l'accès "{selectedAccess?.name}"
            </p>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {[...teamMembers, ...clients].map((user) => (
                <label key={user.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#f8fafc] cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="w-4 h-4 rounded"
                    defaultChecked={selectedAccess?.shared_with?.includes(user.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleShare(selectedAccess.id, [user.id]);
                      } else {
                        handleRevoke(selectedAccess.id, user.id);
                      }
                    }}
                  />
                  <Avatar name={`${user.first_name} ${user.last_name}`} size="sm" />
                  <div>
                    <p className="font-medium text-sm" style={{ color: theme.colors?.primary }}>
                      {user.first_name} {user.last_name}
                    </p>
                    <p className="text-xs text-[#64748b]">{user.role === 'client' ? 'Client' : 'Équipier'}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractDetailPage;
